import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, categoryMap } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  return (
    <>
      <ScrollToTop />
      <CategorySEO category={category} articles={categoryArticles} />
      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="py-8 md:py-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {category.articleCount} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>

        <div className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">分类筛选：</span>
              <div className="flex items-center gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-50 text-foreground-600 hover:text-primary-500 border border-background-200/60'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

function CategorySEO({ category, articles }: { category: typeof categories[0]; articles: typeof import('@/mocks/articles').articles }) {
  const title = `${category.name} | Hitozuma ${category.name} 相关内容解析 - Hitozuma 专题导览`;
  const description = category.description;
  const keywords = `hitozuma,hitozuma ${category.name},hitozuma 文化,hitozuma 内容`;

  return (
    <SEOMeta
      title={title}
      description={description}
      keywords={keywords}
      canonicalUrl={`/category/${category.slug}`}
    />
  );
}